import memoize from 'lodash/memoize';

import config from 'config';
import device from 'common/support/device';
import { KitcenSinkRowItemDataItem, KitchenSinkRowItem, RelatedItem, PregameVideo } from 'store-types';
import { appendLeadingZeroesToNum } from 'common/support/stringHelper';
import { isLiveVideo, isVodContent, isLiveLinear } from 'support/videoHelper';
import { Providers } from 'app-types';

const brand = config.buildBrandId;

const PLATFORM_SAMSUNG_TIZEN = 'samsung-tizen';
const PLATFORM_VIZIO = 'vizio';
const PLATFORM_LG_WEB_OS = 'lg-webos';
const PLATFORM_VEGA = 'vega';

export const getPageValues = (pageName: string) => {
  return {
    pageName: appendPageNamePrefix(pageName),
    siteHier: appendSiteHierPrefix(pageName),
  };
};

export const appendPageNamePrefix = (pageName: string) =>
  `${config.analytics.fields.pageNameBase}${pageName}`;

export const appendSiteHierPrefix = (siteHier: string) =>
  `${config.analytics.fields.siteHierBase}${siteHier}`;

const getPlatformName = () => {
  switch (config.platformName ?? device.platform) {
    case PLATFORM_VIZIO:
      return 'Vizio';
    case PLATFORM_LG_WEB_OS:
      return 'LG';
    case PLATFORM_SAMSUNG_TIZEN:
      return 'Samsung';
    case PLATFORM_VEGA:
      return 'Vega';
    default:
      return 'Unknown';
  }
};

export const getAppId = () =>
  `${config.brand?.name} ${getPlatformName()} build ${
    import.meta.env.VITE_APP_VERSION
  }`;

const getAppData = memoize(async () => {
  let deviceDetails;
  try {
    deviceDetails = await device.getDetail();
  } catch {
    deviceDetails = null;
  }

  return {
    appId: getAppId(),
    osVersion: deviceDetails?.OSVersion || 'UNKNOWN',
    userAgent: device.userAgent,
  };
}, () => config.platformName ?? device.platform);

const getAuthData = memoize(
  (ppUser, mvpdPartner) => {
    const aaSubType = ppUser?.packageStatus;
    const aaUserState = ppUser?.userStatus?.description;
    const aaUserRegId = ppUser?.userId;

    const isAuthenticated = !!(ppUser || mvpdPartner);
    const getUserType = () => {
      if (isAuthenticated) {
        if (ppUser && mvpdPartner) return 'mvpd + all access';
        if (ppUser) return 'all access';
        if (mvpdPartner) return 'mvpd';
      }
      return 'anon';
    };

    return {
      // Always tracked
      userState: isAuthenticated ? 'authenticated' : 'not authenticated',
      userType: getUserType(),

      // Omit the following properties from return value if undefined
      ...(aaSubType && { aaSubType }),
      ...(aaUserState && { aaUserState }),
      ...(aaUserRegId && { aaUserRegId }),
      ...(mvpdPartner && { mvpdPartner }),
    };
  },
  (...args) => JSON.stringify(args),
);

// Get fields that are passed with every tracking call
export const getStaticData = async (ppUser: Record<string, unknown> | null = null, mvpdPartner: string | null = null) => ({
  ...(await getAppData()),
  ...getAuthData(ppUser, mvpdPartner),
});

const getAirDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = appendLeadingZeroesToNum(date.getMonth() + 1, 2);
  const day = appendLeadingZeroesToNum(date.getDate(), 2);
  const hours = appendLeadingZeroesToNum(date.getHours(), 2);
  const minutes = appendLeadingZeroesToNum(date.getMinutes(), 2);
  const seconds = appendLeadingZeroesToNum(date.getSeconds(), 2);

  return `${year}${month}${day} ${hours}:${minutes}:${seconds}`;
};

export const getNielsenData = async () => {
  const rdid = await device.getRDID();
  const isLAT = await device.getLAT();

  let latFromCmp: number | undefined = 0;
  if (
    window.cmp &&
    window.cmp.advertisements.ready &&
    window.cmp.advertisements.getLat() !== undefined
  ) {
    latFromCmp = window.cmp.advertisements.getLat();
  }

  const data: { deviceId?: string; userOptOut?: string | number } = {};
  if (rdid !== 'unknown') data.deviceId = rdid;
  if (isLAT !== 'unknown') {
    data.userOptOut = isLAT;
  } else {
    data.userOptOut = latFromCmp;
  }

  return data;
};

export const getVideoMetadata = (
  video?: KitcenSinkRowItemDataItem | RelatedItem | null,
  displayedVideo?: KitcenSinkRowItemDataItem | RelatedItem | PregameVideo | null,
  row?: KitchenSinkRowItem | null,
  isAutoplay?: boolean,
  stationCode: string | null | undefined = null,
  networkName?: string,
  isCcEnabled?: boolean,
  isCarousel?: boolean,
  geoInfo: { data?: { region?: string; dma?: string; country?: string } } | null | undefined = null,
  subtitleLanguage = 'english', // Default to english until we support other subtitle languages
) => {
  if (!video) return undefined;

  const streamType = getStreamType(video);
  const { c4, c6 } = getComscore(video, stationCode);
  const metadata: {
    streamEntitlement: string;
    videoId?: string;
    name?: string;
    streamType: string;
    mediaPlaylistState: string;
    duration: number | undefined;
    isLive: boolean;
    airDate: string | false;
    c4: unknown;
    c6: unknown;
    closedCaptions: string;
    subtitleLanguage: string;
    mediaAutoPlay?: boolean;
    geoRegion: string | undefined;
    geoDma: string | undefined;
    geoCountry: string | undefined;
    league: string | null | undefined;
    stationCode?: string;
    isDai?: boolean;
    playbackType?: string;
  } = {
    streamEntitlement: getStreamEntitlement(displayedVideo),
    videoId: video.trackingId,
    name: isLiveLinear(video) ? networkName : video.about?.title,
    streamType,
    mediaPlaylistState: getMediaPlayListState(
      displayedVideo?.id,
      row,
      isAutoplay,
    ),
    duration: video.about?.duration,
    isLive: streamType === 'live',
    airDate:
      video.about?.publishedOn
        ? getAirDateString(new Date((video.about?.publishedOn as number) * 1000))
        : false,
    c4,
    c6,
    closedCaptions: isCcEnabled ? 'on' : 'off',
    subtitleLanguage,
    mediaAutoPlay: isCarousel,
    geoRegion: geoInfo?.data?.region,
    geoDma: geoInfo?.data?.dma,
    geoCountry: geoInfo?.data?.country,
    league: video?.properties?.league,
  };

  if (stationCode) {
    metadata.stationCode = stationCode;
  }

  if (video?.ads?.isDai) {
    metadata.isDai = true;
  } else {
    metadata.isDai = false;
  }

  if (video?.ads?.isDai && !video?.playback?.isSyncbak) {
    metadata.playbackType = 'dai';
  } else if (video?.playback?.isSyncbak) {
    metadata.playbackType = 'syncbak';
  } else {
    metadata.playbackType = 'non-dai';
  }

  return metadata;
};

export const getPregameMetadata = (eventId: string | null | undefined = null, eventStatus: string | null | undefined = null) => {
  if (eventId && eventStatus) {
    let segment;
    if (eventStatus === 'pre') {
      segment = 'pre-game';
    } else {
      segment = 'game';
    }
    return {
      eventId,
      eventSegment: segment,
    };
  }
  return {};
};

const getStreamEntitlement = (video?: { authTypes?: string[] } | null) => {
  const authTypes = video?.authTypes || [];
  const isAdobe = authTypes.includes(Providers.ADOBE);
  const isParamountPlus = authTypes.includes(Providers.PARAMOUNT_PLUS);

  if (isAdobe && isParamountPlus) return 'mvpd + all-access';
  if (isAdobe) return 'mvpd';
  if (isParamountPlus) return 'all-access';
  return 'free';
};

const getStreamType = (video?: KitcenSinkRowItemDataItem | RelatedItem | null) => {
  if (isLiveVideo(video)) return 'live';
  if (isVodContent(video)) return 'vod';
  return '';
};

const getMediaPlayListState = (videoId?: string, row?: KitchenSinkRowItem | null, isAutoplay?: boolean) => {
  if (isAutoplay) return 'continuous';
  if (row?.data?.[0]?.id === videoId) return 'first';
  return 'selected';
};

const getComscore = (video?: KitcenSinkRowItemDataItem | RelatedItem | null, stationCode?: string | null) => {
  let c4;
  let c6;
  if (!video) return { c4, c6 };

  if (video.network === 'CBSSN') {
    c4 = 'cbssportsnetwork.com';
    c6 = 'LiveTV-CBS Sports Network';
  } else if (video.network === 'CBSSGOLAZO') {
    c4 = 'CBSSports.com';
    c6 = 'LiveTV-CBS Sports Golazo Network';
  } else if (stationCode) {
    c4 = 'cbs.com';
    c6 = `LiveTV-${stationCode}`;
  } else if (video.authTypes && video.authTypes.includes(Providers.PARAMOUNT_PLUS)) {
    c4 = 'cbs.com';
    c6 = video.about?.title;
  } else if (video.authTypes && video.authTypes.includes(Providers.ADOBE)) {
    c4 = 'CBSSports.com';
    c6 = video.about?.title;
  } else if (video.network === 'CBSSHQ') {
    c4 = 'CBSSports.com';
    c6 = 'LiveTV-CBS Sports HQ';
  } else if (video.network === 'CBSNEWS247') {
    c4 = 'CBSNews.com';
    c6 = 'LiveTV-CBSN Live';
  } else if (video.network === 'CBSNEWSNEWYORK') {
    c4 = 'CBSNewsLocalNY';
    c6 = 'LiveTV-CBSN Local NY';
  } else if (video.network === 'CBSNEWSLOSANGELES') {
    c4 = 'CBSNewsLocalLA';
    c6 = 'LiveTV-CBSN Local LA';
  } else if (video.network === 'CBSNEWSSANFRANCISCO') {
    c4 = 'CBSNewsLocalSanFran';
    c6 = 'LiveTV-CBSN Local San Francisco';
  } else if (video.network === 'CBSNEWSSACRAMENTO') {
    c4 = 'CBSNewsLocalSacramentoCA';
    c6 = 'LiveTV-CBSN Local SacramentoCA';
  } else if (video.network === 'CBSE') {
    c4 = 'etonline.com';
    c6 = 'ET Live Video';
  } else if (
    typeof video.network === 'string' &&
    video.network.includes('CBSNEWS') &&
    video.properties?.type === 'live-linear' &&
    video.network.split('CBSNEWS').pop() !== ''
  ) {
    const station = video.network.split('CBSNEWS').pop();
    c4 = `CBSNewsLocal${station?.[0].toUpperCase()}${station
      ?.slice(1)
      .toLowerCase()}`;
    c6 = `LiveTV-CBSN Local ${station?.[0].toUpperCase()}${station
      ?.slice(1)
      .toLowerCase()}`;
  } else {
    c4 = brand === 'cbs-sports' ? 'CBSSports.com' : 'CBSNews.com';
    c6 =
      video.properties?.type === 'live-linear' && brand === 'cbs-news'
        ? 'LiveTV-CBSN Live'
        : null;
  }

  return { c4, c6 };
};

export const getCarouselMetadata = (isCarousel: boolean, slideNum: number) => {
  if (isCarousel) {
    return {
      moduleName: 'hero carousel:Row1', // This will need to be changed if the Carousel is ever used in another row
      moduleLocation: slideNum,
    };
  }
  return {};
};

export const createBreadCrumb = (historyStack: Array<{ pathname: string }>) => {
  let result = '';
  const regex = /\/.*\//i; // used to remove '/sports/' or '/leagues/'

  historyStack.forEach(entry => {
    const { pathname } = entry;
    if (pathname === '/') {
      result = 'home';
    } else if (pathname === '/playback') {
      // do nothing
    } else {
      const contentId = entry.pathname.split(regex)[1];

      if (contentId) {
        result = result.concat('|').concat(contentId);
      }
    }
  });
  return result;
};

export const getContentRowMetadata = (rowTitle: string | null | undefined = null, rowPos: number | null | undefined = null, rowCol: number | null | undefined = null) => {
  return {
    moduleName: `${rowTitle}:Row${rowPos}`,
    moduleLocation: rowCol,
    moduleAction: 'click',
  };
};

export const getClickedVideoMetadata = (
  isClicked: boolean,
  { rowTitle, rowPos, rowCol }: { rowTitle?: string; rowPos?: number; rowCol?: number },
) => {
  if (isClicked) {
    return getContentRowMetadata(rowTitle, rowPos, rowCol);
  }
  return {};
};
